import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';

import { Sidebar as Sidebar2 } from './components/layout/Sidebar2/Sidebar';
import { Navbar } from './components/Navbar';
import RequireAuth from './components/layout/RequireAuth';
import { ModelsContainer } from './components/layout/ModalsContainer';
import {
  GROWTHBOOK_API_HOST,
  GROWTHBOOK_CLIENT_KEY,
  IS_GROWTBOOK_DEV_MODE_ENABLED,
  IS_GROWTHBOOK_AVAILABLE,
} from './utils/growthbook';
import { AbilityLoader } from './providers/AbilityProvider';

import { useStore } from '@/hooks/useStore';
import { useSidebarToggle } from '@/stores/sidebar';
import { cn } from './lib/utils';
import { AppProvider } from './providers/AppProvider';
import { IntercomProvider } from './providers/IntercomProvider';
import { AnalyticsProvider } from './components/AnalyticsProvider';
import { useIsOnEditor } from './hooks/useIsOnEditor';
import { useCommonStore } from './stores/common';
import { MaintenanceScreen } from './screens/MaintenanceScreen';
// import { SidebarProvider } from './components/ui/sidebar';

const growthbook = new GrowthBook({
  apiHost: GROWTHBOOK_API_HOST,
  clientKey: GROWTHBOOK_CLIENT_KEY,
  enableDevMode: IS_GROWTBOOK_DEV_MODE_ENABLED,
  // trackingCallback: (experiment, result) => {
  //   // TODO: Use your real analytics tracking system
  //   console.log('Viewed Experiment', {
  //     experimentId: experiment.key,
  //     variationId: result.key,
  //   });
  // },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const POPUP_EDITOR_PATH = new RegExp('^/campaigns/[^/]+/popups/[^/]+/design');

const FULL_CONTENT_PATHS = [
  new RegExp('^/mw/inbox'),
  POPUP_EDITOR_PATH,
];

const PATHS_WITH_NO_NAVBAR = [
  POPUP_EDITOR_PATH,
];

export function AppLayout() {
  const location = useLocation();
  const sidebar = useStore(useSidebarToggle, (state) => state);
  const isOnEditor = useIsOnEditor();
  const isMaintenance = useCommonStore(state => state.isMaintenance);

  const isFullContent = FULL_CONTENT_PATHS.some(path => path.test(location.pathname)) || isOnEditor;
  const isNoNavbar = PATHS_WITH_NO_NAVBAR.some(path => path.test(location.pathname));

  useEffect(() => {
    if (IS_GROWTHBOOK_AVAILABLE) {
      growthbook.init({ streaming: true });
    }
  }, []);

  if (isMaintenance) {
    return (
      <MaintenanceScreen />
    );
  }

  return (
    <AppProvider>
      <AnalyticsProvider>
        {/* <SidebarProvider> */}
          <GrowthBookProvider growthbook={growthbook}>
            <QueryClientProvider client={queryClient}>
              <RequireAuth>
                <AbilityLoader>
                  <IntercomProvider>
                      {
                        !isOnEditor ? (
                          <>
                            <Sidebar2 />
                            <main
                              className={cn(
                                "min-h-[calc(100vh_)] transition-[margin-left] ease-in-out duration-300 bg-layout",
                                isOnEditor ? '' : sidebar?.isOpen === false ? "lg:ml-[90px]" : "lg:ml-72",
                                isFullContent ? "h-full" : ""
                              )}
                            >
                              <Navbar visible={!isNoNavbar} />
                              <div className={cn("", {
                                "container pt-8 pb-8 px-4 sm:px-8": !isFullContent,
                                "flex flex-col h-[calc(100vh-theme(spacing.14))]": isFullContent,
                              })}>
                                <Outlet />
                              </div>
                            </main>
                          </>
                        ): <Outlet />
                      }
                      <ModelsContainer />
                  </IntercomProvider>
                </AbilityLoader>
              </RequireAuth>
              {/* <ReactQueryDevtools initialIsOpen={false} /> */}
            </QueryClientProvider>
          </GrowthBookProvider>
        {/* </SidebarProvider> */}
      </AnalyticsProvider>
    </AppProvider>
  );
}
